import AIChat from '@datacloud-incubation/frontend-ai-chat-module';
import {
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  forwardRef,
  type ForwardedRef,
  useRef
} from 'react';
import { animationService } from '@core/services/Animation.service';
import { tanstackQueryService } from '@core/services/TanstackQuery.service';
import { FLOW_CHAT_QUERY_KEYS } from 'AbcloudzFlowChat/constants/flowChat.constant';
import useAbcloudzFlowChatHistory from 'AbcloudzFlowChat/hooks/useAbcloudzFlowChatHistory';
import type { TSessionItem, TSessionMetadata } from 'AbcloudzFlowChat/models/flowChat.model';

const AIChatWrapper = forwardRef(
  (
    {
      flowId,
      selectedSession,
      onSessionCreate,
      updateSessionTitle,
      additionalActions
    }: {
      flowId: string;
      selectedSession: TSessionItem;
      onSessionCreate: () => Promise<void>;
      updateSessionTitle: (sessionMetadata: TSessionMetadata) => void;
      additionalActions?: React.ReactNode[];
    },
    ref: ForwardedRef<{
      clearMessage: () => void;
      setStatus: (value: React.SetStateAction<'idle' | 'pending' | 'succeeded' | 'failed'>) => void;
    }>
  ) => {
    const isMounted = useRef(false);

    const {
      getChatHistory,
      status,
      messages,
      message,
      setMessage,
      onSubmit,
      onStop,
      sessionMetadata,
      setStatus,
      clearMessage,
      isMessageStart
    } = useAbcloudzFlowChatHistory({
      flowId: isMounted.current ? flowId : '',
      selectedSession: isMounted.current ? selectedSession : undefined
    });

    useImperativeHandle(
      ref,
      () => ({
        clearMessage: () => {
          clearMessage();
        },
        setStatus: (value: React.SetStateAction<'idle' | 'pending' | 'succeeded' | 'failed'>) => {
          setStatus(value);
        }
      }),
      [clearMessage, setStatus]
    );

    const memoizedMessages = useMemo(() => {
      return messages.map((msg) => ({
        ...msg,
        session: msg.sessionId,
        created: msg.createdAt
      }));
    }, [messages]);

    const onReloadMemo = useCallback(() => {
      location.reload();
    }, []);

    const onSubmitMessage = useCallback(() => {
      if (selectedSession.id) {
        onSubmit();
      } else {
        void onSessionCreate();
      }
    }, [selectedSession.id, onSubmit, onSessionCreate]);

    useEffect(() => {
      if (sessionMetadata.isComplete) {
        updateSessionTitle(sessionMetadata);
      }
    }, [sessionMetadata, updateSessionTitle]);

    // ---

    const isChatFetching = getChatHistory.isFetching;

    // ---

    useEffect(() => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;

        void tanstackQueryService.queryClient.cancelQueries({
          queryKey: [FLOW_CHAT_QUERY_KEYS.getHistory, flowId, selectedSession.id]
        });
      };
    }, [flowId, selectedSession.id]);

    useEffect(() => {
      if (isChatFetching) {
        animationService.subscribe();
      }

      return () => {
        animationService.unsubscribe();
      };
    }, [isChatFetching]);

    // ---

    return (
      <AIChat
        scrollToBottom
        messages={memoizedMessages}
        status={status}
        isLoading={isChatFetching}
        onChange={setMessage}
        onSubmit={onSubmitMessage}
        onStop={onStop}
        value={message}
        onReload={onReloadMemo}
        isExternalLink
        isMessageStart={isMessageStart}
        additionalActions={additionalActions}
        onFirstItemView={(value) => {
          if (getChatHistory.hasNextPage && value) {
            void getChatHistory.fetchNextPage();
          }
        }}
      />
    );
  }
);

AIChatWrapper.displayName = 'AIChatWrapper';

export default memo(AIChatWrapper);
